import React, { useState } from "react";
import { useBetSlip } from "../providers/BetSlipProvider";
import { useI18n } from "../providers/I18nProvider";
import { ShareChannelEnum } from "../types/enums";

interface BookBetModalProps {
  bookingCode: string;
  validTime: number;
  onClose: () => void;
}

const BookBetModal: React.FC<BookBetModalProps> = ({
  bookingCode,
  validTime,
  onClose,
}) => {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const { config } = useBetSlip();
  const { t } = useI18n();

  const fullBookingCode = "B" + bookingCode;
  const sharedText = "Hey! Click here to see my sport selections at " + config.brand.name;
  const sharedLink = window.location.origin + "/sportsbook/betslip" + "?loadBet=" + fullBookingCode;
  const shareLinks: Record<ShareChannelEnum, string | undefined> = {
    [ShareChannelEnum.WhatsApp]: `https://wa.me/?text=${sharedText}: ${sharedLink}`,
    [ShareChannelEnum.Telegram]: `https://telegram.me/share/url?url=${sharedText}&text=${sharedLink}`,
    [ShareChannelEnum.Facebook]: `https://www.facebook.com/sharer/sharer.php?u=${sharedLink}&quote=${sharedText}`,
    [ShareChannelEnum.CopyLink]: undefined,
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(fullBookingCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShareClick = (channel: ShareChannelEnum) => {
    switch (channel) {
      case ShareChannelEnum.WhatsApp:
      case ShareChannelEnum.Telegram:
      case ShareChannelEnum.Facebook:
        window.open(shareLinks[channel], "_blank", "noopener,noreferrer");
        break;
      case ShareChannelEnum.CopyLink:
        navigator.clipboard.writeText(sharedLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
        break;
    }
  };

  return (
    <div className="flex-1 text-18 bg-white text-gray-900 flex flex-col items-center justify-center pt-6 px-4 pb-3">
        <div className="mb-2">
          {t("BetSlipBookSuccessDescription")}
        </div>
        <div
          className="mb-9 text-gray-800"
          dangerouslySetInnerHTML={{
            __html: t("BetSlipBookSuccessExpirationTimeConfiguration", { validTime })
              .replace(/\{validTime:if-gt\|1\|([^}]*)\}/g, validTime > 1 ? "$1" : ""),
          }}
        />

        {/* Booking Code */}
        <div className="flex items-center gap-7">
          <span className="text-32 text-modal-text font-bold tracking-wider">
            B{bookingCode}
          </span>
          <button
            onClick={handleCopy}
            className={`${copied ? 'bg-btn-book-bet-copied text-btn-book-bet-copied-text' : 'bg-btn-book-bet-copy text-btn-book-bet-copy-text'} text-22 px-5 h-[40px] rounded-4`}
          >
            {copied ? t("BetSlipBookCopied") : t("BetSlipBookCopy")}
          </button>
        </div>

        {/* Share Section */}
        <div className="text-md text-gray-800 my-5">{t("BetSlipShareBet")}</div>
        <div className="flex gap-9 items-center mb-9">
          {/* WhatsApp */}
          <button
            onClick={() => handleShareClick(ShareChannelEnum.WhatsApp)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
          >
            <i style={{ backgroundImage: "url('/assets/share-whatsapp.svg')" }} className="au-m-ico-c w-full h-full"></i>
          </button>

          {/* Telegram */}
          <button
            onClick={() => handleShareClick(ShareChannelEnum.Telegram)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
          >
            <i style={{ backgroundImage: "url('/assets/share-telegram.svg')" }} className="au-m-ico-c w-full h-full"></i>
          </button>

          {/* Facebook */}
          <button
            onClick={() => handleShareClick(ShareChannelEnum.Facebook)}
            className="w-9 h-9 rounded-full flex items-center justify-center"
          >
            <i style={{ backgroundImage: "url('/assets/share-facebook.svg')" }} className="au-m-ico-c w-full h-full"></i>
          </button>

          {/* Link */}
          <div className="relative">
            <button
              onClick={() => handleShareClick(ShareChannelEnum.CopyLink)}
              className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center"
            >
              <i style={{ backgroundImage: "url('/assets/share-link.svg')" }} className="au-m-ico-c w-full h-full"></i>
            </button>
            {linkCopied && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 transition-opacity whitespace-nowrap">
                {t("BetSlipBookCopied")}
              </div>
            )}
          </div>
        </div>

      {/* Ok Button */}
      <button
        className="w-full bg-btn-continue-betting text-btn-continue-betting-text text-22 rounded-4 py-2 h-[45px]"
        onClick={onClose}
      >
        {t("BetSlipBookSuccessContinueBetting")}
      </button>
    </div>
  );
};

export default BookBetModal;
